// frontend/src/components/audit/TenantMultiSelect.tsx
//
// Multi-select de tenants pra view cross-tenant de auditoria (S2-pt1).
// Usado em Auditoria.tsx quando o usuario tem role admin/devops/gestor/
// auditor_global. Estado null = "todos os tenants" (= sem filtro = backend
// default).
//
// Spec: docs/superpowers/specs/2026-05-29-audit-multitenant-view-design.md §4.6
import { MultiSelect } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'

interface TenantOption {
  id: number
  name: string
  is_active: boolean
}

interface AvailableTenantsResponse {
  tenants: TenantOption[]
}

interface Props {
  value: number[] | null    // null = todos os tenants
  onChange: (value: number[] | null) => void
}

export function TenantMultiSelect({ value, onChange }: Props) {
  const { data, isLoading } = useQuery<AvailableTenantsResponse>({
    queryKey: ['audit', 'available-tenants'],
    queryFn: async () => {
      const res = await api.get<AvailableTenantsResponse>('/v1/audit/available-tenants')
      return res.data
    },
    staleTime: 5 * 60 * 1000, // 5min cache
  })

  const options = (data?.tenants ?? []).map((t) => ({
    value: String(t.id),
    label: t.is_active ? t.name : `${t.name} (inativo)`,
  }))

  return (
    <MultiSelect
      label="Tenants"
      placeholder="Todos os tenants"
      data={options}
      value={(value ?? []).map(String)}
      onChange={(vals) => {
        const ids = vals.map(Number).filter((n) => Number.isFinite(n))
        onChange(ids.length === 0 ? null : ids)
      }}
      searchable
      clearable
      disabled={isLoading}
      nothingFoundMessage="Nenhum tenant encontrado"
      maxDropdownHeight={300}
      w={360}
    />
  )
}
